import { computed, getCurrentInstance, ref, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useOnboardingSurface } from 'dashboard/composables/useOnboardingSurface';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  PAGE_COACH,
  coachForRoute,
  routeViewOf,
  coachMode,
  seenVersionOf,
} from 'dashboard/helper/onboardingRegistry';
import { tourBlocksCoach } from 'dashboard/composables/useGuidedTour';

// Drawer do manual: estado compartilhado entre PageCoach, PageManual e o
// item Ajuda do cluster. Não vive em ui_settings (é só a sessão aberta).
const pageManualOpen = ref(false);
// Chave explicita (central de ajuda abre o manual de outra tela); null = tela atual.
const pageManualKey = ref(null);

export function openPageManual(key = null) {
  pageManualKey.value = key && PAGE_COACH[key] ? key : null;
  pageManualOpen.value = true;
}

export function closePageManual() {
  pageManualOpen.value = false;
  pageManualKey.value = null;
}

export function usePageCoach({ host = true } = {}) {
  const route = useRoute();
  const { uiSettings, updateUISettings } = useUISettings();

  const pageCoach = computed(() => {
    const raw = uiSettings.value?.page_coach;
    const fromStore = raw && typeof raw === 'object' ? raw : {};
    const seenRaw =
      fromStore.seen &&
      typeof fromStore.seen === 'object' &&
      !Array.isArray(fromStore.seen)
        ? fromStore.seen
        : {};
    const seen = {};
    Object.entries(seenRaw).forEach(([key, value]) => {
      const version = seenVersionOf(value);
      if (version != null) seen[key] = version;
    });
    return {
      seen,
      disabled: fromStore.disabled === true,
    };
  });

  const { enabled: surfaceEnabled } = useOnboardingSurface();
  const currentCoach = computed(() =>
    coachForRoute(route.name, routeViewOf(route))
  );
  // Manual a exibir: a chave pedida pela central de ajuda ou a tela atual.
  const manualCoach = computed(() =>
    pageManualKey.value
      ? PAGE_COACH[pageManualKey.value] || currentCoach.value
      : currentCoach.value
  );

  const mode = computed(() => {
    const coach = currentCoach.value;
    if (!coach || pageCoach.value.disabled) return null;
    return coachMode(coach, pageCoach.value.seen[coach.key]);
  });

  const shouldShow = computed(() => mode.value !== null);

  const active = ref(false);
  const activeKey = ref(null);
  const activeSelector = ref('');
  const floating = ref(false);

  const patchPageCoach = partial => {
    const nextSeen =
      partial.seen && typeof partial.seen === 'object'
        ? partial.seen
        : pageCoach.value.seen;
    updateUISettings({
      page_coach: {
        seen: { ...nextSeen },
        disabled:
          typeof partial.disabled === 'boolean'
            ? partial.disabled
            : pageCoach.value.disabled,
      },
    });
  };

  const closeActive = () => {
    active.value = false;
    activeKey.value = null;
    activeSelector.value = '';
    floating.value = false;
  };

  // Pedido do Lucas em 26/08/2026: a dica NAO abre mais sozinha ao entrar na
  // tela (aparecia "a mensagemzinha do mascote" em toda pagina). O conteudo
  // continua acessivel pelo manual (Ajuda desta tela / central de ajuda) e
  // pelo tour; o registro versionado segue valendo para eles.
  const evaluateRoute = () => {
    closeActive();
  };

  const markSeen = (key, version) => {
    const coach = (key && coachForRoute(key)) || currentCoach.value;
    const target = key || coach?.key;
    if (!target) return;
    const numeric = Number(version);
    const nextVersion =
      Number.isFinite(numeric) && numeric > 0 ? numeric : coach?.version || 1;
    patchPageCoach({
      seen: { ...pageCoach.value.seen, [target]: nextVersion },
    });
    if (activeKey.value === target) closeActive();
  };

  const dismissAll = () => {
    patchPageCoach({ disabled: true });
    closeActive();
  };

  const resetAll = () => {
    patchPageCoach({ seen: {}, disabled: false });
  };

  const openManual = () => {
    const coach = currentCoach.value;
    if (coach) markSeen(coach.key, coach.version);
    pageManualOpen.value = true;
  };

  const closeManual = () => {
    pageManualOpen.value = false;
    pageManualKey.value = null;
  };

  if (host) {
    watch(
      () => [route.name, routeViewOf(route)],
      () => {
        evaluateRoute();
      },
      { immediate: true }
    );

    // Ajuda só existe no desktop web: se a superfície deixar de valer no meio
    // (janela estreitada, PWA), dica e manual fecham como o tour guiado já faz.
    watch(surfaceEnabled, enabled => {
      if (!enabled) {
        closeActive();
        closeManual();
        return;
      }
      evaluateRoute();
    });

    watch(
      () => [
        pageCoach.value.disabled,
        pageCoach.value.seen,
        mode.value,
        tourBlocksCoach.value,
      ],
      () => {
        if (tourBlocksCoach.value) {
          closeActive();
          return;
        }
        if (!active.value) {
          evaluateRoute();
          return;
        }
        if (!shouldShow.value) closeActive();
      },
      { deep: true }
    );

    if (getCurrentInstance()) {
      onBeforeUnmount(() => {
        closeActive();
      });
    }
  }

  return {
    pageCoach,
    currentCoach,
    manualCoach,
    mode,
    shouldShow,
    active,
    activeKey,
    activeSelector,
    floating,
    markSeen,
    dismissAll,
    resetAll,
    patchPageCoach,
    evaluateRoute,
    closeActive,
    manualOpen: pageManualOpen,
    openManual,
    closeManual,
  };
}
